import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { joyApi } from "../api/joyApi";
import JoyButton from "../components/JoyButton";
import FloatingStars from "../components/FloatingStars";

export default function Login() {
  const nav = useNavigate();
  const { login, testLogin, testAllowed, authenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [inactive, setInactive] = useState(false);

  useEffect(() => {
    if (authenticated) nav("/", { replace: true });
  }, [authenticated, nav]);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setInactive(false);
    try {
      await joyApi.requestCode(email.trim().toLowerCase());
      setStep(2);
      toast.success("Código enviado! Verifique seu e-mail 💌");
    } catch (err) {
      toast.error("Ops! Não foi possível enviar agora. Tente novamente. 🦊");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (code.trim().length < 4) return;
    setLoading(true);
    try {
      const res = await joyApi.verifyCode(email.trim().toLowerCase(), code.trim());
      if (res.success && res.token && res.user_id) {
        login(res.token, res.user_id, email.trim().toLowerCase());
        toast.success("Bem-vindo à aventura! 🌟");
        nav("/", { replace: true });
      } else {
        toast.error(res.message || "Código inválido. Tente novamente.");
      }
    } catch (err) {
      toast.error("Não foi possível verificar o código agora. 🦊");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex justify-center py-0 sm:py-6">
      <div
        className="relative w-full max-w-[500px] min-h-screen sm:min-h-[calc(100vh-3rem)] bg-[#FFF8E7] overflow-hidden sm:rounded-[32px]"
        style={{ boxShadow: "0 20px 60px rgba(255,105,180,0.25)" }}
      >
        <FloatingStars />
        <div className="relative z-10 px-6 pt-14 pb-10 flex flex-col items-center">
          <div className="relative joy-bob">
            <img
              src="/joy.png"
              alt="Joy"
              className="w-32 h-32 rounded-full object-cover border-4 border-[#FF69B4]"
              style={{ boxShadow: "0 12px 30px rgba(255,105,180,0.4)" }}
            />
            <span className="absolute -top-1 -right-1 text-2xl joy-twinkle">💕</span>
          </div>
          <h1 className="mt-5 text-3xl font-black text-[#FF69B4] text-center leading-tight">
            Joy's English Adventure
          </h1>
          <p className="text-[#8B7355] font-bold mt-1 mb-8">Aprenda inglês com a Joy! ✨</p>

          {inactive ? (
            <div
              data-testid="inactive-access"
              className="w-full bg-white rounded-[24px] p-6 text-center border-2 border-[#FFC0CB]"
            >
              <div className="text-4xl mb-2">🔒</div>
              <p className="font-extrabold text-[#4A3728] mb-2">Seu acesso não está ativo no momento.</p>
              <p className="text-[#8B7355] font-semibold text-sm">
                Se você acredita que isso é um erro, entre em contato com o suporte.
              </p>
              <JoyButton variant="white" className="mt-5" full onClick={() => { setInactive(false); setStep(1); }}>
                ← Voltar
              </JoyButton>
            </div>
          ) : step === 1 ? (
            <form onSubmit={requestCode} className="w-full bg-white rounded-[24px] p-6 border-2 border-[#FFE0EC] joy-slide-in">
              <label className="block font-extrabold text-[#4A3728] mb-2 text-center">
                Digite o e-mail usado na sua compra
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="w-full rounded-[18px] border-2 border-[#FFC0CB] bg-[#FFF8E7] px-4 py-3 font-bold text-[#4A3728] outline-none focus:border-[#FF69B4] mb-4 text-center"
                style={{ minHeight: 52 }}
                required
              />
              <JoyButton
                type="submit"
                variant="pink"
                full
                disabled={loading}
                data-testid="request-code-button"
              >
                {loading ? "Enviando..." : "Receber meu código 💌"}
              </JoyButton>
            </form>
          ) : (
            <form onSubmit={verifyCode} className="w-full bg-white rounded-[24px] p-6 border-2 border-[#FFE0EC] joy-slide-in">
              <p className="font-extrabold text-[#4A3728] mb-1 text-center">
                Enviamos um código para o seu e-mail 💌
              </p>
              <p className="text-xs text-[#8B7355] font-semibold text-center mb-4">{email}</p>
              <input
                data-testid="login-code-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="______"
                className="w-full rounded-[18px] border-2 border-[#FFC0CB] bg-[#FFF8E7] px-4 py-3 font-black text-2xl tracking-[0.5em] text-[#4A3728] outline-none focus:border-[#FF69B4] mb-4 text-center"
                style={{ minHeight: 56 }}
                required
              />
              <JoyButton type="submit" variant="pink" full disabled={loading} data-testid="verify-code-button">
                {loading ? "Entrando..." : "Entrar na aventura 🌟"}
              </JoyButton>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full mt-3 text-sm font-bold text-[#8B7355] underline"
                data-testid="change-email-button"
              >
                Usar outro e-mail
              </button>
            </form>
          )}

          {testAllowed && !inactive && (
            <button
              type="button"
              data-testid="test-login-button"
              onClick={() => { testLogin(); nav("/", { replace: true }); }}
              className="mt-6 text-xs font-bold text-[#9B59B6] underline opacity-80"
            >
              Entrar como convidado (modo de teste) 🧪
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
